import { useRef } from "react";
import "./MessageBodyEditor.css";
import {
  ComposeState,
  MessageTemplate,
  MESSAGE_PLACEHOLDERS,
  renderBody,
} from "../../../models/MessageModels";

interface MessageBodyEditorProps {
  compose: ComposeState;
  templates: MessageTemplate[];
  orgName: string;
  previewClient: { fullName: string; phone: string } | null;
  onChange: (next: ComposeState) => void;
}

const MessageBodyEditor = ({
  compose,
  templates,
  orgName,
  previewClient,
  onChange,
}: MessageBodyEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const selectedTemplate =
    templates.find((t) => t.id === compose.templateId) ?? null;
  const bodyValue = selectedTemplate ? selectedTemplate.body : compose.body;
  const usingTemplate = selectedTemplate !== null;

  const insertPlaceholder = (token: string) => {
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? compose.body.length;
    const end = ta?.selectionEnd ?? compose.body.length;
    const next =
      compose.body.slice(0, start) + token + compose.body.slice(end);
    onChange({ ...compose, body: next });
    requestAnimationFrame(() => {
      ta?.focus();
      const pos = start + token.length;
      ta?.setSelectionRange(pos, pos);
    });
  };

  const preview = renderBody(bodyValue, {
    ClientName: previewClient?.fullName,
    OrgName: orgName || "{OrgName}",
    Phone: previewClient?.phone,
  });

  return (
    <>
      <div className="msg-editor__field">
        <span className="msg-editor__label">Template</span>
        <select
          value={compose.templateId ?? ""}
          onChange={(e) => {
            const id = e.target.value || null;
            const template = templates.find((t) => t.id === id);
            onChange({
              ...compose,
              templateId: id,
              body: template ? template.body : compose.body,
            });
          }}
        >
          <option value="">No template — free text</option>
          {templates
            .filter((t) => t.isActive)
            .map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
        </select>
      </div>

      <div className="msg-editor__field">
        <span className="msg-editor__label">Message body</span>
        <textarea
          ref={textareaRef}
          rows={6}
          value={bodyValue}
          disabled={usingTemplate}
          placeholder="Type your message or pick a template…"
          onChange={(e) => onChange({ ...compose, body: e.target.value })}
        />
        {usingTemplate && (
          <span className="msg-editor__hint">
            Using the selected template body.
          </span>
        )}
      </div>

      <div className="msg-editor__field">
        <span className="msg-editor__label">Insert placeholder</span>
        <div className="msg-editor__chips">
          {MESSAGE_PLACEHOLDERS.map(({ token, label }) => (
            <button
              key={token}
              type="button"
              className="msg-editor__chip"
              disabled={usingTemplate}
              title={`Insert ${token} (${label})`}
              onClick={() => insertPlaceholder(token)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="msg-editor__field">
        <span className="msg-editor__label">Live preview</span>
        <div className="msg-editor__preview">
          {preview ? (
            preview
          ) : (
            <span className="msg-editor__muted">
              Preview appears once you type a message.
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export default MessageBodyEditor;